/**
 * Filter Search Component
 *
 * Client-side text filter for long filter lists (e.g. Brand). Rendered by
 * snippets/filter-search-box.liquid as the first child of a group's
 * [portal:source] wrapper, immediately before the <ul> of checkbox options.
 *
 * @component filter-search
 * @extends HTMLElement
 *
 * @behavior
 * - Matches the typed query against each option's visible label text.
 * - Hides the non-matching <li> elements (visual only — it never touches the
 *   inputs' `.hidden` class, so active-filter counts and the AJAX payload are
 *   unaffected, and a hidden-but-checked brand stays applied).
 * - Moves with the portal, so the same instance works in the mobile panel and
 *   the desktop dropdown without re-initialisation.
 *
 * @markup
 * <div portal:source="filter.p.vendor">
 *   <filter-search><label><input ...></label></filter-search>
 *   <ul><li>...</li></ul>
 * </div>
 */
class FilterSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.onInput = this.handleInput.bind(this);
    this.input.addEventListener('input', this.onInput);
  }

  /**
   * The option list lives next to this element inside the shared portal wrapper.
   * Resolve it lazily so it keeps working after the portal moves us around.
   */
  get list() {
    return this.parentElement?.querySelector('ul') ?? null;
  }

  handleInput() {
    const query = this.input.value.trim().toLowerCase();
    const list = this.list;
    if (!list) return;

    for (const item of list.children) {
      // The label text is the last <span> in the row; fall back to full text.
      const labelEl = item.querySelector('label span:last-child') ?? item;
      const label = labelEl.textContent.trim().toLowerCase();
      const matches = query === '' || label.includes(query);
      item.hidden = !matches;
    }
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
  }
}

if (!customElements.get('filter-search')) {
  customElements.define('filter-search', FilterSearch);
}
